/* eslint-disable jsx-a11y/anchor-is-valid */
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Disclaimer,
  Div,
  EditPenDarkBlueIcon,
  H2,
  Icon,
  IconButtonStyled,
  P,
  Separator,
  TooltipWrapper,
  VDriveBalancePills,
  VDriveWhiteIcon,
  centsToDollars,
  formatter,
  getLocationVDriveById,
  lightTheme,
  queryKeys,
  reportError,
  useVDrive,
} from '@vgrubs/components';
import {
  LocationVDriveEntity,
  SimpleLocationEntity,
} from 'components/src/types/location.types';
import { Dispatch, SetStateAction, useState } from 'react';
import styled from 'styled-components';
import { useReceivablesLocation } from '../Receivables/hooks/useReceivablesLocation';
import { EditBalance } from './onboarding/EditBalance';
import { EditCard } from './onboarding/EditCard';
import { NO_PENDING_BANK } from './vDriveHelpers';
import { VDriveCreditCardEditBalance } from './VDriveCreditCardEditBalance';

export const UserCreditCardStyled = styled.div`
  padding: 20px 24px;
  background: ${({ theme }) => theme.darkBlue};
  border-radius: 30px;
  display: inline-flex;
  flex-direction: column;
  justify-content: space-around;
  flex: none;
  width: 100%;
  /* max-width: 450px;
  min-width: 450px;
  min-height: 270px; */
  aspect-ratio: 16/10;

  @media (max-width: 800px) {
    padding: 16px 20px;

    /* padding: 24px; */
    /* max-width: 400px;
    min-width: auto;
    min-height: 240px; */
  }
`;
export const UserCreditCardAlertStyled = styled.div`
  padding: 16px 20px;
  background: ${({ theme }) => theme.primary};
  border-radius: 0 0 30px 30px;
  flex: none;
  width: calc(100% - 60px);
  margin: 0 30px;

  @media (max-width: 800px) {
    padding: 16px 20px;
    margin: 0 30px;
  }
`;

export const VDriveCreditCard = ({
  location,
  balance,
  setBalance,
  isVDriveCard,
  setLoading,
  vDriveVerification,
  handleGetVDrive,
  cardFailed,
}: {
  location: SimpleLocationEntity | undefined;
  balance: number;
  setBalance: Dispatch<SetStateAction<number>>;
  /** Still on the vGrubs vDrive card, which is being retired. */
  isVDriveCard: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  vDriveVerification: LocationVDriveEntity | undefined;
  handleGetVDrive: () => void;
  cardFailed: boolean;
}) => {
  const [editModal, setEditModal] = useState(false);
  const [editBalance, setEditBalance] = useState(false);
  const [editBalanceModal, setEditBalanceModal] = useState(false);
  // The fees for being below zero and the 3-day pause are only in what the
  // location owes (the receivables detail, cached by Credit Cards too).
  const locationId = Number(location?.id);
  const owed = useReceivablesLocation(locationId).detail?.current.vDrive;
  const queryClient = useQueryClient();
  const setLocationVDrive = useVDrive((state) => state.setLocationVDrive);

  // After adding or editing the balance. Never the old balance plus the
  // amount: while below zero the backend takes the fees out of a top-up
  // first, so only it knows the new balance - and whether the fee is paid.
  const refreshBalance = async () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.admin.receivables.location(locationId),
    });
    try {
      setLoading(true);
      const { data } = await getLocationVDriveById(locationId);
      const { totalBalance, reservedBalance, cardFailed } = data.data;
      setBalance((totalBalance || 0) - (reservedBalance || 0));
      // Only the money: the rest of the store may hold the page's own edits.
      const current = useVDrive.getState().locationVDrive;
      if (current) {
        setLocationVDrive({
          ...current,
          totalBalance,
          reservedBalance,
          cardFailed,
        });
      }
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <UserCreditCardStyled>
        <Div $flex $alignCenter $between $gap="50px" $gapSM="8px">
          <Icon $size={130} $sizeSM={90}>
            <VDriveWhiteIcon />
          </Icon>
          <Div $flex $column $alignEnd>
            <P $right $mb="4" $color={lightTheme.white}>
              Client’s vDrive Balance
            </P>
            <Div $flex $alignCenter $between $gap="12px">
              <H2 $right $m="0" $color={lightTheme.white}>
                {formatter.format(balance ? centsToDollars(balance) : 0)}
              </H2>
            </Div>
            {/* Derived from the balance on screen rather than the stored
                outstandingAmount; both modals refetch it once they save. */}
            <VDriveBalancePills
              negativeBalance={Math.max(-balance, 0)}
              overdraftLimit={vDriveVerification?.overdraftLimit ?? 0}
              pendingBank={vDriveVerification?.pendingBank ?? NO_PENDING_BANK}
              negativeFeeDue={owed?.negativeFeeDue}
              negativePaused={owed?.negativePaused}
            />
            {isVDriveCard && (
              <TooltipWrapper tooltip="Still on the vGrubs vDrive card, which is being retired - it stops being charged on the sunset date. Ask the owner to add their own card.">
                <Div $mt="12" $mtSM="8">
                  <VDriveCardSvg />
                </Div>
              </TooltipWrapper>
            )}
          </Div>
        </Div>
        <Separator $m="20px 0" $mSM="12px 0" $color={lightTheme.white + '80'} />
        <Div $flex $alignCenter $between $gap="24px" $gapSM="8px">
          {/* With an own card on file the cards are managed in the Credit
              Cards section under this one; here only the first is added. */}
          {isVDriveCard || !vDriveVerification?.last4 ? (
            <Div
              $flex
              $alignCenter
              $gap="12px"
              $gapSM="8px"
              $pointer
              onClick={() => setEditModal(true)}
            >
              <IconButtonStyled $size="34px" $color={lightTheme.white}>
                <Icon>
                  <EditPenDarkBlueIcon />
                </Icon>
              </IconButtonStyled>
              <Disclaimer $m="0" $color={lightTheme.white}>
                {isVDriveCard ? 'vGrubs vDrive card' : 'No card on file'}
                <br />
                Add Card
              </Disclaimer>
            </Div>
          ) : (
            <span />
          )}

          <Button
            $selected={editBalance}
            $whiteDarkBlue
            $w="150px"
            $wSM="125px"
            onClick={() => setEditBalance(true)}
          >
            Add Balance
          </Button>
        </Div>
      </UserCreditCardStyled>
      <EditCard
        modalIsOpen={editModal}
        closeModal={() => setEditModal(false)}
        id={location?.id?.toString() ?? ''}
        setLoading={setLoading}
        handleGetVDrive={handleGetVDrive}
      />
      <EditBalance
        modalIsOpen={editBalance}
        closeModal={() => setEditBalance(false)}
        id={location?.id?.toString() ?? ''}
        onAdded={refreshBalance}
        setLoading={setLoading}
      />

      <VDriveCreditCardEditBalance
        editBalanceModal={editBalanceModal}
        setEditBalanceModal={setEditBalanceModal}
        id={location?.id?.toString() ?? ''}
        onSaved={refreshBalance}
        setLoading={setLoading}
        balance={balance}
      />

      {cardFailed && (
        <UserCreditCardAlertStyled>
          <Disclaimer $center $mb="16" $color={lightTheme.white}>
            We attempted to charge client’s card, but it didn’t go through.
            Please check card details or contact customer. Until then, top-ups
            come from the location’s vGrubs balance, then the bank.
          </Disclaimer>
          <P
            $bold
            $center
            $mb="0"
            $underline
            $pointer
            $color={lightTheme.white}
            onClick={() => setEditModal(true)}
          >
            Update Client’s Card
          </P>
        </UserCreditCardAlertStyled>
      )}
    </div>
  );
};

const VDriveCardSvg = () => (
  <svg
    width="116"
    height="29"
    viewBox="0 0 116 29"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.3496 11.6855L14.5518 19.3027H13.248L10.4429 11.6855H11.7832L13.8853 17.9478H13.9146L16.0239 11.6855H17.3496ZM18.5508 8.73389H22.103C25.2378 8.73389 27.0908 10.6895 27.0908 14.0146C27.0908 17.3472 25.2451 19.3027 22.103 19.3027H18.5508V8.73389ZM19.8691 9.92041V18.1162H22.0151C24.4102 18.1162 25.7432 16.6514 25.7432 14.0293C25.7432 11.3853 24.4175 9.92041 22.0151 9.92041H19.8691ZM28.5337 19.3027V11.6855H29.7349V12.9307H29.7642C30.0132 12.0884 30.7163 11.5537 31.6099 11.5537C31.8296 11.5537 32.02 11.5903 32.1226 11.605V12.8428C32.02 12.8062 31.749 12.7695 31.4414 12.7695C30.4526 12.7695 29.8081 13.48 29.8081 14.5566V19.3027H28.5337ZM33.2285 19.3027V11.6855H34.5029V19.3027H33.2285ZM33.8657 10.4038C33.4336 10.4038 33.0747 10.0449 33.0747 9.61279C33.0747 9.17334 33.4336 8.82178 33.8657 8.82178C34.3052 8.82178 34.6641 9.17334 34.6641 9.61279C34.6641 10.0449 34.3052 10.4038 33.8657 10.4038ZM42.4717 11.6855L39.6738 19.3027H38.3701L35.5649 11.6855H36.9053L39.0073 17.9478H39.0366L41.146 11.6855H42.4717ZM46.3096 12.6597C45.1157 12.6597 44.2808 13.5752 44.2002 14.8423H48.3384C48.3091 13.5679 47.5107 12.6597 46.3096 12.6597ZM48.3091 17.1201H49.5615C49.3418 18.4531 48.0527 19.4346 46.3901 19.4346C44.2075 19.4346 42.8818 17.9185 42.8818 15.5234C42.8818 13.1577 44.2295 11.5537 46.3315 11.5537C48.3896 11.5537 49.6567 13.0479 49.6567 15.355V15.8384H44.1929V15.9116C44.1929 17.3691 45.0498 18.3286 46.4194 18.3286C47.3862 18.3286 48.0967 17.8379 48.3091 17.1201ZM58.5703 19.4785C55.6553 19.4785 53.8096 17.3618 53.8096 14.0146C53.8096 10.6968 55.6772 8.55811 58.5703 8.55811C60.8701 8.55811 62.6206 9.95703 62.9575 12.0664H61.6245C61.2876 10.6821 60.0791 9.77393 58.5703 9.77393C56.5049 9.77393 55.1646 11.4438 55.1646 14.0146C55.1646 16.6074 56.4902 18.2627 58.5776 18.2627C60.1304 18.2627 61.2437 17.5156 61.6245 16.2266H62.9575C62.4741 18.314 60.9067 19.4785 58.5703 19.4785ZM66.832 18.3506C68.0332 18.3506 68.9561 17.5083 68.9561 16.4097V15.7871L66.9199 15.9116C65.8945 15.9775 65.3086 16.4316 65.3086 17.1494C65.3086 17.8745 65.9165 18.3506 66.832 18.3506ZM66.5464 19.4346C65.0303 19.4346 64.0122 18.519 64.0122 17.1494C64.0122 15.8457 65.0156 15.0254 66.7734 14.9229L68.9561 14.7983V14.1611C68.9561 13.231 68.3408 12.6743 67.2861 12.6743C66.3047 12.6743 65.6895 13.1357 65.5283 13.8828H64.2979C64.3857 12.5645 65.5137 11.5537 67.3154 11.5537C69.1025 11.5537 70.2305 12.5205 70.2305 14.0366V19.3027H69.022V17.9917H68.9927C68.5386 18.8633 67.5718 19.4346 66.5464 19.4346ZM71.8491 19.3027V11.6855H73.0503V12.9307H73.0796C73.3286 12.0884 74.0317 11.5537 74.9253 11.5537C75.145 11.5537 75.3354 11.5903 75.438 11.605V12.8428C75.3354 12.8062 75.0645 12.7695 74.7568 12.7695C73.7681 12.7695 73.1235 13.48 73.1235 14.5566V19.3027H71.8491ZM78.9976 19.4346C77.0566 19.4346 75.7383 17.8818 75.7383 15.4941C75.7383 13.1064 77.0493 11.5537 78.9683 11.5537C80.0596 11.5537 80.9385 12.0884 81.3926 12.9233H81.4219V8.73389H82.6963V19.3027H81.4805V18.0137H81.4585C80.9678 18.8926 80.0962 19.4346 78.9976 19.4346ZM79.2393 12.6743C77.8916 12.6743 77.042 13.7729 77.042 15.4941C77.042 17.2227 77.8916 18.314 79.2393 18.314C80.5503 18.314 81.4292 17.2007 81.4292 15.4941C81.4292 13.7949 80.5503 12.6743 79.2393 12.6743Z"
      fill="white"
    />
    <path
      d="M100.024 6.30274C95.6057 6.30274 92.0244 9.88399 92.0244 14.3027C92.0244 18.7215 95.6057 22.3027 100.024 22.3027C104.443 22.3027 108.024 18.7215 108.024 14.3027C108.024 9.88399 104.443 6.30274 100.024 6.30274ZM100.024 18.3027C99.6088 18.3027 99.2744 17.9684 99.2744 17.5527L99.2744 14.0527C99.2744 13.6371 99.6088 13.3027 100.024 13.3027C100.44 13.3027 100.774 13.6371 100.774 14.0527L100.774 17.5527C100.774 17.9684 100.44 18.3027 100.024 18.3027ZM99.0244 11.3027C99.0244 10.7496 99.4713 10.3027 100.024 10.3027C100.578 10.3027 101.024 10.7496 101.024 11.3027C101.024 11.8559 100.578 12.3027 100.024 12.3027C99.4713 12.3027 99.0244 11.8559 99.0244 11.3027Z"
      fill="white"
    />
    <rect
      opacity="0.5"
      x="0.774414"
      y="0.552734"
      width="114.5"
      height="27.5"
      rx="13.75"
      stroke="white"
      strokeWidth="0.5"
    />
  </svg>
);
